import {
  memo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useOutletContext } from "react-router-dom";
import readNDJSONStream from "ndjson-readablestream";
import { v4 as uuidv4 } from "uuid";
import { useMsal } from "@azure/msal-react";
import { Beaker20Regular } from "@fluentui/react-icons";

import styles from "./Chat.module.css";
import mbenergy_logo from "../../assets/mbenergy_logo.png";

import {
  chatApi,
  errorMessageFromResponse,
  summarizeChatApi,
  ChatAppResponse,
  ChatAppRequest,
  ResponseMessage,
  deleteAllUploadedFilesApi,
  ProcessStep,
  createPromptFromConversationApi,
  ConversationPromptResponse,
  PromptGenerationUpdate,
} from "../../api";
import { useLogin } from "../../utils/authConfig";
import { getToken } from "../../utils/authConfig";
import { Answers, ToolInfo, SiteMappingOption } from "../../api/models";
import { Answer, AnswerLoading } from "../../features/chat/Answer";
import { getUserFriendlyError } from "../../utils/errorMapper";
import {
  QuestionInput,
  QuestionInputHandle,
} from "../../features/question-input-container/QuestionInput";
import SendButton from "../../features/question-input-container/SendButton/SendButton";
import { UserChatMessage } from "../../features/chat/UserChatMessage";
import {
  AnalysisPanel,
  AnalysisPanelTabs,
} from "../../features/chat/AnalysisPanel";
import { UploadButton } from "../../features/upload/UploadButton/UploadButton";
import UploadPopup from "../../features/upload/UploadPopup/UploadPopup";
import FileChips, {
  FileChipItem,
} from "../../features/upload/FileChips/FileChips";
import { useFileUpload } from "../../features/upload/useFileUpload";
import { IndexedDB } from "../../features/indexedDB/IndexedDB";
import { HistoryPanel } from "../../features/chat-history/HistorySidePanel";
import AnswerModeContainer from "../../features/answer-modes/AnswerModeContainer/AnswerModeContainer";
import {
  AnswerMode,
  defaultAnswerMode,
} from "../../features/answer-modes/AnswerModeContainer/AnswerMode";
import ModelSettingsPopup, {
  ReasoningEffort,
  getReasoningValueForModel,
  ModelConfigEntry,
  MODEL_CONFIG,
  normalizeReasoningEffortForModel,
} from "../../features/question-input-container/ModelSettingsPopup/ModelSettingsPopup";
import ModelSettingsButton from "../../features/question-input-container/ModelSettingsButton/ModelSettingsButton";
import ToolSelector from "../../features/question-input-container/ToolSelector/ToolSelector";
import CreateAnswerModeModal from "../../features/answer-modes/CreateAnswerModeModal/CreateAnswerModeModal";
import { useUseCase } from "../../context/UseCaseContext/UseCaseContext";
import { GenerateStreamingType } from "../layout/Layout";
import { CustomAnswerModeDB } from "../../features/indexedDB/CustomAnswerModeDB";
import { readJsonStream } from "../../utils/streamUtils";
import LocationSelector from "../../features/question-input-container/LocationSelector/LocationSelector";
import LocationSelectorButton from "../../features/question-input-container/LocationSelector/LocationSelectorButton";

const customDB = new CustomAnswerModeDB();

// -----------------------------------------------------------------------
// HELPER FUNCTIONS FOR DATE SEPARATORS
// -----------------------------------------------------------------------

const getDateFromTimestamp = (
  timestamp: string | number | undefined | null,
): Date | null => {
  if (!timestamp) return null;

  // Handle number (integer) timestamps
  if (typeof timestamp === "number") {
    // Python often sends seconds (10 digits), JS needs milliseconds (13 digits).
    // If the number is small (likely seconds), multiply by 1000.
    if (timestamp < 10000000000) {
      return new Date(timestamp * 1000);
    }
    return new Date(timestamp);
  }

  // Handle string (ISO) timestamps from legacy data if any
  const date = new Date(timestamp);
  return isNaN(date.getTime()) ? null : date;
};

const isSameDay = (d1: Date, d2: Date) => {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const getSeparatorLabel = (date: Date) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const datePart = `${day}.${month}.${year}`;

  if (isSameDay(date, today)) {
    return `Today, ${datePart}`;
  } else if (isSameDay(date, yesterday)) {
    return `Yesterday, ${datePart}`;
  } else {
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    return `${weekday}, ${datePart}`;
  }
};

type InteractionHandlers = {
  setUserMessageRef: (index: number, el: HTMLDivElement | null) => void;
  showCitation: (citation: string, index: number) => void;
  toggleTab: (tab: AnalysisPanelTabs, index: number) => void;
  edit: (question: string) => void;
  retry: (question: string) => void;
};

type ChatInteractionProps = {
  index: number;
  interaction: [string, ChatAppResponse];
  answerMode: AnswerMode;
  separatorLabel: string;
  isLastItem: boolean;
  showLoading: boolean;
  isStreaming: boolean;
  isSelected: boolean;
  disableEdit: boolean;
  error?: string;
  reasoningResponse: string;
  showReasoning: boolean;
  handlers: InteractionHandlers;
};

// One question and its answer. Memoized: while an answer streams, only its own row re-renders,
// not every earlier question and answer of the chat.
const ChatInteraction = memo(function ChatInteraction({
  index,
  interaction,
  answerMode,
  separatorLabel,
  isLastItem,
  showLoading,
  isStreaming,
  isSelected,
  disableEdit,
  error,
  reasoningResponse,
  showReasoning,
  handlers,
}: ChatInteractionProps) {
  const question = interaction[0];
  const processSteps = interaction[1]?.choices?.[0]?.context?.process_steps || [];

  const setUserMessageRef = useCallback(
    (el: HTMLDivElement | null) => handlers.setUserMessageRef(index, el),
    [handlers, index],
  );
  const onCitationClicked = useCallback(
    (c: string) => handlers.showCitation(c, index),
    [handlers, index],
  );
  const onThoughtProcessClicked = useCallback(
    () => handlers.toggleTab(AnalysisPanelTabs.ThoughtProcessTab, index),
    [handlers, index],
  );
  const onSupportingContentClicked = useCallback(
    () => handlers.toggleTab(AnalysisPanelTabs.SupportingContentTab, index),
    [handlers, index],
  );
  const onRegenerate = useCallback(() => handlers.edit(question), [handlers, question]);
  const onRetry = useCallback(() => handlers.retry(question), [handlers, question]);

  return (
    <div>
      {separatorLabel && (
        <div className={styles.dateSeparator}>{separatorLabel}</div>
      )}

      <div ref={setUserMessageRef}>
        <UserChatMessage
          message={question}
          isLast={isLastItem}
          onEdit={handlers.edit}
          disableEdit={disableEdit}
        />
      </div>

      {showLoading ? (
        <div className={styles.chatMessageGpt}>
          <AnswerLoading
            answerMode={answerMode}
            reasoningResponse={reasoningResponse}
            showReasoningSummary={showReasoning}
            processSteps={processSteps}
          />
        </div>
      ) : (
        <div className={styles.chatMessageGpt}>
          <Answer
            isStreaming={isStreaming}
            answer={interaction[1]}
            isSelected={isSelected}
            onCitationClicked={onCitationClicked}
            onThoughtProcessClicked={onThoughtProcessClicked}
            onSupportingContentClicked={onSupportingContentClicked}
            answerMode={answerMode}
            onRegenerate={onRegenerate}
            isLast={isLastItem}
            error={error}
            onRetry={onRetry}
          />
        </div>
      )}
    </div>
  );
});

const Chat = () => {
  // -----------------------------------------------------------------------
  // 1. STATE & CONFIG
  // -----------------------------------------------------------------------
  const { useCases, selectedUseCase } = useUseCase();
  const { handleGenerationStreaming } =
    useOutletContext<GenerateStreamingType>();
  const client = useLogin ? useMsal().instance : undefined;
  const { accounts } = useMsal();

  const lastQuestionRef = useRef<string>("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const modelSettingsPopupButtonRef = useRef<HTMLButtonElement>(null);
  const locationSelectorButtonRef = useRef<HTMLButtonElement>(null);

  const [sessionState, setSessionState] = useState<string>(uuidv4());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const [answers, setAnswers] = useState<
    [user: string, response: ChatAppResponse][]
  >([]);
  const [streamedAnswers, setStreamedAnswers] = useState<
    [user: string, response: ChatAppResponse][]
  >([]);

  const [reasoningResponse, setReasoningResponse] = useState<string>("");
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
  const [activeCitation, setActiveCitation] = useState<string>();
  const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<
    AnalysisPanelTabs | undefined
  >(undefined);
  const [selectedAnswer, setSelectedAnswer] = useState<number>(0);
  const [historyPanelVersion, setHistoryPanelVersion] = useState<number>(0);
  const refreshHistoryPanel = () => setHistoryPanelVersion((v) => v + 1);
  const [historySelected, setHistorySelected] = useState<boolean>(false);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);

  const [answerMode, setAnswerMode] = useState<AnswerMode[]>([]);
  const [selectedAnswerMode, setSelectedAnswerMode] = useState<AnswerMode>(
    defaultAnswerMode({}),
  );
  const [answerModeQuestion, setAnswerModeQuestion] = useState<string>("");
  const [showAnswerModeContainer, setShowAnswerModeContainer] =
    useState<boolean>(selectedUseCase === "GENERIC");
  const [isGridExpanded, setIsGridExpanded] = useState<boolean>(false);
  const [isChildModalOpen, setIsChildModalOpen] = useState<boolean>(false);
  const [placeholder, SetPlaceholder] = useState<string>("");
  const questionInputRef = useRef<QuestionInputHandle>(null);
  const [canSend, setCanSend] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  const [modelSettingsPopupOpen, setModelSettingsPopupOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [showModelSettings, setShowModelSettings] = useState<boolean>(false);
  const [model, setModel] = useState<ModelConfigEntry>(
    MODEL_CONFIG["gpt-6.1-sol"] ??
      MODEL_CONFIG["gpt-5.6-terra"],
  );
  const [reasoningEffort, setReasoningEffort] = useState<string>(
    getReasoningValueForModel("gpt-6.1-sol", ReasoningEffort.Normal),
  );
  const [showReasoning, setShowReasoning] = useState<boolean>(true);

  // Get available tools for current use case
  const [availableTools, setAvailableTools] = useState<ToolInfo[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);

  const [availableSiteMappings, setAvailableSiteMappings] = useState<SiteMappingOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>(["auto"]);
  const [locationSelectorOpen, setLocationSelectorOpen] = useState<boolean>(false);
  const [showUserUpload, setShowUserUpload] = useState<boolean>(false);
  const [uploadPopupOpen, setUploadPopupOpen] = useState<boolean>(false);
  // Track files that are currently uploading (shown with spinners)
  const [pendingFiles, setPendingFiles] = useState<string[]>([]);
  const [numDocuments, setNumDocuments] = useState<number>(0);
  const [uploadedFilepaths, setUploadedFilepaths] = useState<string[]>([]);
  const [uploadedFilenames, setUploadedFilenames] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [indexedDBInstance, setIndexedDBInstance] = useState<IndexedDB>(
    new IndexedDB("chat-database", "chat-history", selectedUseCase),
  );
  const [stateSnapshot, setStateSnapshot] = useState<Record<
    string,
    any
  > | null>(null);

  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [promptModalInitialMode, setPromptModalInitialMode] =
    useState<AnswerMode | null>(null);
  const [isExtractingPrompt, setIsExtractingPrompt] = useState(false);
  const [externalDraft, setExternalDraft] =
    useState<ConversationPromptResponse | null>(null);
  const [extractionProgress, setExtractionProgress] =
    useState<PromptGenerationUpdate | null>(null);
  const [answerModeRefreshKey, setAnswerModeRefreshKey] = useState(0);

  // -----------------------------------------------------------------------
  // 2. SCROLLING
  // -----------------------------------------------------------------------
  const uploadButtonRef = useRef(null);
  const userMessageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastScrolledLength = useRef<number>(0);
  const targetScrollPosition = useRef<number | null>(null);
  const isScrollLocked = useRef<boolean>(false);
  const interactions =
    isLoading || isStreaming || streamedAnswers.length > 0
      ? streamedAnswers
      : answers;

  const scrollEnforcementRef = useRef<number | null>(null);
  const answerResizeObserver = useRef<ResizeObserver | null>(null);
  const isUserInteracting = useRef(false);
  const isAutoScrolling = useRef(false);

  const forceScrollTo = (
    container: HTMLElement,
    targetTop: number,
    duration = 300,
  ) => {
    const startTime = performance.now();
    if (scrollEnforcementRef.current)
      cancelAnimationFrame(scrollEnforcementRef.current);

    const loop = () => {
      if (isUserInteracting.current) return;

      container.scrollTo({ top: targetTop, behavior: "auto" });

      if (performance.now() - startTime < duration) {
        scrollEnforcementRef.current = requestAnimationFrame(loop);
      }
    };

    scrollEnforcementRef.current = requestAnimationFrame(loop);
  };

  const getAnswerElement = (userMessageIndex: number) => {
    const userMsg = userMessageRefs.current[userMessageIndex];
    if (!userMsg) return null;
    return userMsg.nextElementSibling;
  };

  useLayoutEffect(() => {
    const currentLength = streamedAnswers.length;
    const container = chatContainerRef.current;

    if (!container || currentLength === 0) return;

    // Trigger logic if length increased OR if we are explicitly loading/streaming a new message (edit case)
    if (
      currentLength > lastScrolledLength.current ||
      (isLoading && currentLength === lastScrolledLength.current)
    ) {
      const lastIndex = currentLength - 1;

      requestAnimationFrame(() => {
        const userMsg = userMessageRefs.current[lastIndex];
        const answerEl = getAnswerElement(lastIndex);

        if (userMsg && answerEl) {
          const containerHeight = container.clientHeight;
          const answerHeight = answerEl.clientHeight;
          const neededPadding = Math.max(0, containerHeight - answerHeight);

          container.style.paddingBottom = `${neededPadding}px`;

          isAutoScrolling.current = true;
          isUserInteracting.current = false;

          if (targetScrollPosition.current !== null) {
            targetScrollPosition.current = userMsg.offsetTop;
          }

          // Force scroll to the User Message (top)
          forceScrollTo(container, userMsg.offsetTop, 500);

          setTimeout(() => {
            isAutoScrolling.current = false;
          }, 550);
        }
      });

      lastScrolledLength.current = currentLength;
    }
  }, [streamedAnswers.length, isLoading]);

  useEffect(() => {
    if (!isStreaming || streamedAnswers.length === 0) {
      if (answerResizeObserver.current)
        answerResizeObserver.current.disconnect();
      return;
    }

    const lastIndex = streamedAnswers.length - 1;
    const answerEl = getAnswerElement(lastIndex);
    const container = chatContainerRef.current;

    if (answerEl && container) {
      if (answerResizeObserver.current)
        answerResizeObserver.current.disconnect();

      answerResizeObserver.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const answerHeight = entry.contentRect.height;
          const containerHeight = container.clientHeight;
          const neededPadding = Math.max(0, containerHeight - answerHeight);
          container.style.paddingBottom = `${neededPadding}px`;
        }
      });

      answerResizeObserver.current.observe(answerEl);
    }

    return () => {
      if (answerResizeObserver.current)
        answerResizeObserver.current.disconnect();
    };
  }, [isStreaming, streamedAnswers.length]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const breakLock = () => {
      isUserInteracting.current = true;
      if (scrollEnforcementRef.current)
        cancelAnimationFrame(scrollEnforcementRef.current);
    };

    container.addEventListener("wheel", breakLock, { passive: true });
    container.addEventListener("touchstart", breakLock, { passive: true });
    container.addEventListener("keydown", breakLock, { passive: true });

    return () => {
      container.removeEventListener("wheel", breakLock);
      container.removeEventListener("touchstart", breakLock);
      container.removeEventListener("keydown", breakLock);
    };
  }, []);

  useEffect(() => {
    if (!isScrollLocked.current || targetScrollPosition.current === null)
      return;

    const container = chatContainerRef.current;
    if (!container) return;

    const intervalId = setInterval(() => {
      if (isAutoScrolling.current) return;

      if (isScrollLocked.current && targetScrollPosition.current !== null) {
        const currentScroll = container.scrollTop;
        const target = targetScrollPosition.current;

        if (Math.abs(currentScroll - target) > 50) {
          container.scrollTo({
            top: target,
            behavior: "smooth",
          });
        }
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [isStreaming, isLoading]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;

    const paddingBottomVal = parseInt(container.style.paddingBottom || "0", 10);
    const realScrollHeight = container.scrollHeight - paddingBottomVal;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = realScrollHeight - scrollTop - clientHeight;
    const isUserAtBottom = distanceFromBottom <= 20;

    if (isAtBottom !== isUserAtBottom) {
      setIsAtBottom(isUserAtBottom);
    }

    const isOverflowing = realScrollHeight > clientHeight;
    const shouldShowButton = isOverflowing && !isUserAtBottom;

    if (showScrollToBottom !== shouldShowButton) {
      setShowScrollToBottom(shouldShowButton);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "auto",
      });
    }
    setShowScrollToBottom(false);
  };

  // -----------------------------------------------------------------------
  // 3. ADAPTIVE STREAMING RENDERER
  // -----------------------------------------------------------------------
  // Yields control to the browser so rAF, React commits, paint, and input
  // can run between synchronous bursts of work. Uses scheduler.yield() when
  // available (Chrome 129+), falls back to MessageChannel (no 4ms clamp,
  // unlike setTimeout(0)).
  const yieldToMain: () => Promise<void> = (() => {
    const s = (globalThis as any).scheduler;
    if (s && typeof s.yield === "function") {
      return () => s.yield();
    }
    const channel = new MessageChannel();
    const pending: Array<() => void> = [];
    channel.port1.onmessage = () => {
      const resolve = pending.shift();
      if (resolve) resolve();
    };
    return () =>
      new Promise<void>((resolve) => {
        pending.push(resolve);
        channel.port2.postMessage(null);
      });
  })();

  const handleAsyncRequest = async (
    question: string,
    responseBody: ReadableStream<any>,
    signal: AbortSignal,
  ) => {
    let capturedError: string | undefined = undefined;
    let answerStarted = false;

    // --- 1. STATE BUFFER ---
    const buffer = {
      fullText: "",
      displayedText: "",
      fullReasoning: "",
      displayedReasoning: "",
      processSteps: [] as ProcessStep[],
      stepsHaveChanged: false,
      networkFinished: false,
      contextChanged: false,
      stateSnapshot: null as Record<string, any> | null,
      visualizations: {} as Record<
        string,
        {
          language: string;
          code: string;
          description: string;
          validated: boolean;
        }
      >,
      visualizationsChanged: false,

      baseResponse: {
        choices: [
          {
            message: { content: "", role: "assistant" },
            context: { data_points: [], thoughts: null, process_steps: [] },
            session_state: undefined,
          },
        ],
      } as any,
    };

    // --- 2. NETWORK INGESTION ---
    const readStream = async () => {
      try {
        for await (const event of readNDJSONStream(responseBody)) {
          if (signal.aborted) break;
          if (event.error) throw new Error(event.error);

          if (event.role === "visualization_data") {
            buffer.visualizations = event.content;
            buffer.visualizationsChanged = true;
            // Yield so the UI can pick up the viz change.
            await yieldToMain();
            continue;
          }

          if (event.role === "state_snapshot") {
            buffer.stateSnapshot = event.content;
            continue;
          }

          const choice = event?.choices?.[0];
          if (!choice) continue;

          if (choice.context?.data_points) {
            buffer.baseResponse = event;
            buffer.contextChanged = true;
          }

          if (choice.session_state) {
            buffer.baseResponse.choices[0].session_state = choice.session_state;
          }

          if (choice.delta?.step_update) {
            const newStep = choice.delta.step_update as ProcessStep;
            if (newStep.step_id) {
              const existingIndex = buffer.processSteps.findIndex(
                (s) => s.step_id === newStep.step_id,
              );
              if (existingIndex !== -1) {
                buffer.processSteps[existingIndex] = newStep;
              } else {
                buffer.processSteps.push(newStep);
              }
            } else {
              buffer.processSteps.push(newStep);
            }
            buffer.stepsHaveChanged = true;
          }

          const delta = choice.delta || {};
          const content = delta.content || "";

          // "system" carries notices such as the content-filter message; show them as the answer.
          const isAnswerText = delta.role === "assistant" || delta.role === "system";
          if (content && isAnswerText && !answerStarted) {
            answerStarted = true;
            setIsLoading(false);
          }

          if (delta.role === "summary" && content) {
            buffer.fullReasoning += content;
          } else if (isAnswerText && content) {
            buffer.fullText += content;
          }

          // === THE FIX ===
          // Yield to the event loop so rAF, React commits, paint, and input
          // can interleave between NDJSON events. Without this, a burst of
          // 16+ chunks arriving in a single TCP packet runs as one long
          // synchronous task, starving the UI render loop.
          await yieldToMain();
        }
      } catch (e) {
        if (signal.aborted) return;
        const rawMessage = e instanceof Error ? e.message : String(e);
        const friendlyMessage = getUserFriendlyError(rawMessage);
        capturedError = friendlyMessage;
        setError(friendlyMessage);
        setIsLoading(false);
        setIsStreaming(false);
      } finally {
        buffer.networkFinished = true;
      }
    };

    const readPromise = readStream();

    await new Promise<void>((resolve) => {
      const BASE_CPS = 200;
      const MAX_CPS = 600;
      const MAX_BURST_CHARS = 80;

      const SMOOTHING_ATTACK = 0.95;
      const SMOOTHING_RELEASE = 0.8;
      const QUEUE_AT_MAX_SPEED = 1500;

      const MIN_COMMIT_INTERVAL_MS = 33;
      const MIN_CHARS_DELTA = 2;

      let lastTs = performance.now();
      let rafId = 0;
      let smoothedQueue = 0;
      let lastCommitTs = 0;
      let lastCommittedLen = 0;

      let cachedStepsRef = buffer.processSteps;
      let cachedVizRef = buffer.visualizations;
      let cachedBaseRef = buffer.baseResponse;
      let cachedContext: any = null;

      const tick = (ts: number) => {
        if (signal.aborted) {
          resolve();
          return;
        }

        const dt = ts - lastTs;
        lastTs = ts;

        const textQueue = buffer.fullText.length - buffer.displayedText.length;
        const reasoningQueue =
          buffer.fullReasoning.length - buffer.displayedReasoning.length;

        const smoothing =
          textQueue > smoothedQueue ? SMOOTHING_ATTACK : SMOOTHING_RELEASE;
        smoothedQueue = smoothing * smoothedQueue + (1 - smoothing) * textQueue;

        const isTextDone = textQueue <= 0;
        const isReasoningDone = reasoningQueue <= 0;

        let textChanged = false;
        let stepsChanged = false;
        let vizChanged = false;
        let contextChanged = false;

        if (!isTextDone) {
          let effectiveCps: number;

          if (buffer.networkFinished) {
            effectiveCps = MAX_CPS * 4;
          } else {
            const loadRatio = Math.min(1, smoothedQueue / QUEUE_AT_MAX_SPEED);
            effectiveCps = BASE_CPS + (MAX_CPS - BASE_CPS) * loadRatio;
          }

          const charsThisFrame = Math.max(
            1,
            Math.min(
              textQueue,
              MAX_BURST_CHARS,
              Math.ceil((effectiveCps * dt) / 1000),
            ),
          );

          buffer.displayedText = buffer.fullText.slice(
            0,
            buffer.displayedText.length + charsThisFrame,
          );
          textChanged = true;
        }

        // CHANGED: recompute after reveal so the rest of the tick sees the
        // up-to-date state. Fixes the short-response hang where the last few
        // chars revealed in this tick never got committed because isTextDone
        // was stale (computed before reveal).
        const isTextDoneAfterReveal =
          buffer.displayedText.length >= buffer.fullText.length;
        const hasUncommittedText =
          buffer.displayedText.length !== lastCommittedLen;

        if (!isReasoningDone) {
          buffer.displayedReasoning = buffer.fullReasoning;
          setReasoningResponse(buffer.displayedReasoning);
        }

        if (buffer.stepsHaveChanged) {
          buffer.stepsHaveChanged = false;
          cachedStepsRef = buffer.processSteps.slice();
          stepsChanged = true;
        }

        if (buffer.visualizationsChanged) {
          buffer.visualizationsChanged = false;
          cachedVizRef = buffer.visualizations;
          vizChanged = true;
        }

        if (buffer.contextChanged) {
          buffer.contextChanged = false;
          cachedBaseRef = buffer.baseResponse;
          contextChanged = true;
        }

        const shouldEmit =
          textChanged || stepsChanged || vizChanged || contextChanged;

        const structuralChange = stepsChanged || vizChanged || contextChanged;
        const sinceLastCommit = ts - lastCommitTs;
        const charsSinceCommit = buffer.displayedText.length - lastCommittedLen;

        // CHANGED: final-flush detection now uses the post-reveal flag, and
        // also fires whenever there's uncommitted text and the stream is done.
        const isFinalFlush =
          buffer.networkFinished && isTextDoneAfterReveal && hasUncommittedText;

        // CHANGED: hasUncommittedText also lets shouldCommit fire when text
        // finished but no other "emit" reason is set. Prevents the loop from
        // sitting on uncommitted final chars.
        const shouldCommit =
          (shouldEmit || hasUncommittedText) &&
          (structuralChange ||
            isFinalFlush ||
            (charsSinceCommit >= MIN_CHARS_DELTA &&
              sinceLastCommit >= MIN_COMMIT_INTERVAL_MS));

        if (shouldCommit) {
          const safeChoice = cachedBaseRef.choices?.[0] || {};

          if (contextChanged || stepsChanged || vizChanged || !cachedContext) {
            cachedContext = {
              ...(safeChoice.context || {}),
              process_steps: cachedStepsRef,
              visualizations: cachedVizRef,
            };
          }

          const uiPayload: ChatAppResponse = {
            ...cachedBaseRef,
            choices: [
              {
                ...safeChoice,
                message: { content: buffer.displayedText, role: "assistant" },
                context: cachedContext,
              },
            ],
          };

          setStreamedAnswers((prev) => {
            const len = prev.length;
            if (len === 0) return prev;
            const next = prev.slice();
            next[len - 1] = [question, uiPayload];
            return next;
          });

          lastCommitTs = ts;
          lastCommittedLen = buffer.displayedText.length;
        }

        // CHANGED: termination uses the post-reveal flag too.
        if (
          buffer.networkFinished &&
          isTextDoneAfterReveal &&
          isReasoningDone &&
          !buffer.stepsHaveChanged &&
          !buffer.contextChanged &&
          !buffer.visualizationsChanged &&
          buffer.displayedText.length === lastCommittedLen
        ) {
          resolve();
          return;
        }

        rafId = requestAnimationFrame(tick);
      };

      rafId = requestAnimationFrame(tick);

      signal.addEventListener(
        "abort",
        () => {
          cancelAnimationFrame(rafId);
          resolve();
        },
        { once: true },
      );
    });

    await readPromise;

    setIsStreaming(false);
    setIsLoading(false);

    // --- FINAL RETURN ---
    const finalChoice = buffer.baseResponse.choices?.[0] || {};

    return {
      ...buffer.baseResponse,
      choices: [
        {
          ...finalChoice,
          message: { content: buffer.displayedText, role: "assistant" },
          context: {
            ...(finalChoice.context || {}),
            process_steps: buffer.processSteps,
            visualizations: buffer.visualizations,
          },
        },
      ],
      error: capturedError,
      stateSnapshot: buffer.stateSnapshot,
    } as ChatAppResponse & {
      error?: string;
      stateSnapshot?: Record<string, any> | null;
    };
  };

  const makeApiRequest = async (
    question: string,
    answersOverride?: [string, ChatAppResponse][],
    answerModeOverride?: AnswerMode,
  ) => {
    const controller = new AbortController();
    setAbortController(controller);
    lastQuestionRef.current = question;

    error && setError(undefined);
    setIsLoading(true);
    setIsStreaming(true);
    setActiveCitation(undefined);
    setActiveAnalysisPanelTab(undefined);
    setReasoningResponse("");

    const currentAnswers = answersOverride || answers;
    const effectiveAnswerMode = answerModeOverride || selectedAnswerMode;
    const currentSessionState =
      currentAnswers[currentAnswers.length - 1]?.[1]?.choices?.[0]
        ?.session_state ?? sessionState;
    const currentStateSnapshot =
      (
        currentAnswers[currentAnswers.length - 1]?.[1] as
          | (ChatAppResponse & { stateSnapshot?: Record<string, any> | null })
          | undefined
      )?.stateSnapshot ?? stateSnapshot;

    const normalizedAnswerMode: AnswerMode = {
      id: effectiveAnswerMode.id,
      name: effectiveAnswerMode.name,
      color: effectiveAnswerMode.color,
      icon: effectiveAnswerMode.icon,
      orderIndex: effectiveAnswerMode.orderIndex,
      isCustom: effectiveAnswerMode.isCustom,
      disable: effectiveAnswerMode.disable,
      contextRequirement: effectiveAnswerMode.contextRequirement,
      mode: effectiveAnswerMode.mode,
      model: model.name,
      reasoningEffort: normalizeReasoningEffortForModel(
        model.name,
        reasoningEffort,
      ),
      placeholder: effectiveAnswerMode.placeholder,
      documentMode: effectiveAnswerMode.documentMode,
      default_question: effectiveAnswerMode.default_question,
      description: effectiveAnswerMode.description,
      roleDefinition: effectiveAnswerMode.roleDefinition,
      taskDescription: effectiveAnswerMode.taskDescription,
      outputFormat: effectiveAnswerMode.outputFormat,
      examples: effectiveAnswerMode.examples,
    };

    const placeholderResponse: ChatAppResponse = {
      choices: [
        {
          index: 0,
          message: { content: "", role: "assistant" },
          session_state: currentSessionState,
          context: { data_points: [], thoughts: null },
          timestamp: Math.floor(Date.now() / 1000),
        },
      ],
    };

    setStreamedAnswers([...currentAnswers, [question, placeholderResponse]]);

    const token = client ? await getToken(client) : undefined;

    // Replace visualization placeholders (e.g. <<viz:viz_b731b4ae>>)
    // with their actual code from context.visualizations.
    const expandVisualizationPlaceholders = (
      content: string,
      visualizations?: Record<
        string,
        {
          language: string;
          code: string;
          description: string;
          validated: boolean;
        }
      >,
    ): string => {
      if (!content || !visualizations) return content;

      return content.replace(/<<viz:([a-zA-Z0-9_-]+)>>/g, (match, vizId) => {
        const viz = visualizations[vizId];
        if (!viz) return match;

        return (
          `[${vizId} — type: ${viz.language} | use source_viz_ids=["${vizId}"] to modify]\n` +
          `\`\`\`${viz.language}\n${viz.code}\n\`\`\``
        );
      });
    };

    try {
      const messages: ResponseMessage[] = currentAnswers.flatMap(
        ([userQ, resp]) => {
          const choice = resp.choices[0];
          const rawAssistantContent = choice.message.content ?? "";
          const visualizations = (choice.context as any)?.visualizations as
            | Record<
                string,
                {
                  language: string;
                  code: string;
                  description: string;
                  validated: boolean;
                }
              >
            | undefined;

          const cleanedAssistantContent = expandVisualizationPlaceholders(
            rawAssistantContent,
            visualizations,
          );

          return [
            { content: userQ, role: "user" as const },
            { content: cleanedAssistantContent, role: "assistant" as const },
          ];
        },
      );

      const request: ChatAppRequest = {
        messages: [...messages, { content: question, role: "user" }],
        session_state: currentSessionState,
        answer_mode: normalizedAnswerMode,
        model: model.name,
        tools: selectedTools,
        ...(currentStateSnapshot
          ? { state_snapshot: currentStateSnapshot }
          : {}),
        ...(uploadedFilenames.length > 0 ? { fileNames: uploadedFilenames } : {}),
        ...(availableSiteMappings.length > 0 &&
        selectedSites.length > 0 &&
        !selectedSites.includes("auto")
          ? { selectedSites }
          : {}),
      };

      const response = await chatApi(
        selectedUseCase,
        request,
        token,
        controller.signal,
      );
      if (!response.ok) {
        throw new Error(await errorMessageFromResponse(response));
      }
      if (!response.body) throw Error("No response body");

      const fullResponse = await handleAsyncRequest(
        question,
        response.body,
        controller.signal,
      );

      // Capture snapshot for next turn
      if (fullResponse.stateSnapshot) {
        setStateSnapshot(fullResponse.stateSnapshot);
      }

      const newAnswerState: [string, ChatAppResponse][] = [
        ...currentAnswers,
        [question, fullResponse],
      ];
      setAnswers(newAnswerState);
      setSessionState(fullResponse.choices[0].session_state);

      const chatId = fullResponse.choices[0].session_state;
      await indexedDBInstance.addItem(
        chatId,
        newAnswerState,
        [...answerMode.slice(0, currentAnswers.length), normalizedAnswerMode],
        uploadedFilepaths,
        uploadedFilenames,
        uploadedImages,
        undefined,
        undefined,
        fullResponse.stateSnapshot ?? null,
        selectedSites,
      );

      // The title comes from a separate request: save the chat first, add the title when it arrives.
      const conversationLength = newAnswerState.length;
      if (conversationLength === 1 || conversationLength % 5 === 0) {
        const db = indexedDBInstance;
        void summarizeChat(newAnswerState).then(async (title) => {
          if (!title) return;
          await db.updateTitle(chatId, title);
          refreshHistoryPanel();
        });
      }
    } catch (e: any) {
      const rawMessage = e instanceof Error ? e.message : String(e);
      const friendlyMessage = getUserFriendlyError(rawMessage);

      setError(friendlyMessage);
      setIsLoading(false);
      setIsStreaming(false);

      const failedAnswerState: [string, ChatAppResponse][] = [
        ...currentAnswers,
        [question, placeholderResponse],
      ];

      setAnswers(failedAnswerState);

      await indexedDBInstance.addItem(
        sessionState,
        failedAnswerState,
        [...answerMode.slice(0, currentAnswers.length), normalizedAnswerMode],
        uploadedFilepaths,
        uploadedFilenames,
        uploadedImages,
        undefined,
        friendlyMessage,
        currentStateSnapshot ?? null,
        selectedSites,
      );
    } finally {
      refreshHistoryPanel();
      setSelectedAnswerMode((prev) => ({
        ...defaultAnswerMode({}),
        model: prev.model,
        reasoningEffort: normalizeReasoningEffortForModel(
          prev.model ?? model.name,
          prev.reasoningEffort,
        ),
      }));
    }
  };

  const handleSend = (question: string) => {
    setIsGridExpanded(false);
    makeApiRequest(question);
    setAnswerMode([
      ...answerMode,
      {
        ...selectedAnswerMode,
        model: model.name,
        reasoningEffort: normalizeReasoningEffortForModel(model.name, reasoningEffort),
      },
    ]);
  };

  const handleEditLastMessage = (newQuestion: string) => {
    if (isLoading || isStreaming) return;

    // 1. Capture the mode used for the last message BEFORE slicing
    // Fallback to selectedAnswerMode if history is empty for some reason
    const modeToReuse = answerMode[answerMode.length - 1] || selectedAnswerMode;

    // 2. Remove the last message from local state
    const trimmedAnswers = answers.slice(0, -1);
    setAnswers(trimmedAnswers);
    setStreamedAnswers(trimmedAnswers);

    // Remove the last mode from the history tracking array
    setAnswerMode((prev) => prev.slice(0, -1));

    // Optional: Visually update the UI dropdown to match the mode being regenerated
    setSelectedAnswerMode(modeToReuse);

    // 3. Trigger request passing:
    //    - The new question
    //    - The trimmed history
    //    - The specific mode to reuse
    makeApiRequest(newQuestion, trimmedAnswers, modeToReuse);

    // 4. Update answer mode history
    // We add back the mode we are about to use
    setAnswerMode((prev) => [...prev, modeToReuse]);
  };

  const handleCreatePromptFromConversation = async () => {
    if (!interactions.length) return;

    // Open modal immediately so user sees progress inside it
    setIsPromptModalOpen(true);
    setIsExtractingPrompt(true);
    setExtractionProgress({
      node: "start",
      message: "Analysing conversation...",
      step: 0,
      total_steps: 4,
    });

    try {
      const token = client ? await getToken(client) : undefined;

      const conversation = interactions.flatMap(([question, response]) => [
        { role: "user", content: question },
        {
          role: "assistant",
          content: response.choices[0].message.content ?? "",
        },
      ]);

      const res = await createPromptFromConversationApi(
        { messages: conversation },
        token,
      );

      if (!res.ok) throw new Error(await res.text());
      if (!res.body) throw new Error("No response body");

      const reader = res.body.getReader();

      await readJsonStream<PromptGenerationUpdate>(reader, (update) => {
        setExtractionProgress(update);

        if (update.result) {
          setExternalDraft(update.result); // raw ConversationPromptResponse
          setTimeout(() => {
            setIsExtractingPrompt(false);
            setExtractionProgress(null);
          }, 600);
        }
      });
    } catch (err) {
      console.error("Failed to extract prompt from conversation", err);
      setIsExtractingPrompt(false);
      setExtractionProgress(null);
    }
  };

  const handlePromptModalSave = async (mode: AnswerMode) => {
    // 1. Persist to IndexedDB
    await customDB.addOrUpdateMode(mode);

    // 2. Prepend to global order so it appears first
    const currentOrder = (await customDB.getGlobalOrder()) ?? [];
    await customDB.saveGlobalOrder([mode.id, ...currentOrder]);

    // 3. Close modal and clear draft state
    setIsPromptModalOpen(false);
    setPromptModalInitialMode(null);
    setExternalDraft(null);

    // 4. Trigger AnswerModeContainer to refetch
    setAnswerModeRefreshKey((k) => k + 1);
  };

  // -----------------------------------------------------------------------
  // 4. EFFECTS & HELPERS
  // -----------------------------------------------------------------------
  const firstName = useMemo(() => {
    const account = accounts?.[0];
    if (!account) return "";
    // idTokenClaims often has given_name
    const givenName = (account.idTokenClaims as any)?.given_name;
    if (givenName) return givenName;
    // Fallback: split the full name
    const fullName = account.name || "";
    return fullName.split(" ")[0] || "";
  }, [accounts]);

  useEffect(() => {
    handleGenerationStreaming(isLoading || isStreaming);
  }, [isLoading, isStreaming]);

  useEffect(() => {
    if (selectedUseCase != "") {
      newChat();
      updateAnswerModeUi(selectedUseCase);
      setIndexedDBInstance(
        new IndexedDB("chat-database", "chat-history", selectedUseCase),
      );

      // Every use case starts with gpt-6.1-sol (or gpt-5.6-terra) and normal reasoning.
      setReasoningEffort(
        getReasoningValueForModel("gpt-6.1-sol", ReasoningEffort.Normal),
      );
      setModel(MODEL_CONFIG["gpt-6.1-sol"] ?? MODEL_CONFIG["gpt-5.6-terra"]);
      setShowReasoning(true);
      setShowUserUpload(useCases[selectedUseCase].showUserUpload);
      setShowModelSettings(useCases[selectedUseCase].showModelSettings);
      setAvailableModels(useCases[selectedUseCase].availableModels);
      setAvailableTools(useCases[selectedUseCase].availableTools);
      setAvailableSiteMappings(useCases[selectedUseCase].siteMappings ?? []);
      setSelectedTools([]); // Select first 2 tools by default
      setSelectedSites(["auto"]);
    }
    // The configuration is loaded once by UseCaseProvider; switching use cases doesn't reload it.
  }, [selectedUseCase]);

  const updateAnswerModeUi = (useCase: string) => {
    setShowAnswerModeContainer(useCase === "GENERIC");
  };

  useEffect(() => {
    if (uploadedFilepaths.length != numDocuments)
      setNumDocuments(uploadedFilepaths.length);
  }, [uploadedFilepaths]);

  useEffect(() => {
    if (sessionState) fetchUploadedDocuments(sessionState);
  }, [sessionState]);

  useEffect(() => {
    if (historySelected) {
      lastScrolledLength.current = 0;
      targetScrollPosition.current = null;
      isScrollLocked.current = false;
      userMessageRefs.current = [];

      if (chatContainerRef.current) {
        chatContainerRef.current.style.paddingBottom = "0px";
      }

      setTimeout(() => {
        scrollToBottom();
      }, 50);

      setHistorySelected(false);
    }
  }, [historySelected]);

  useEffect(() => {
    SetPlaceholder(selectedAnswerMode.placeholder);
    setAnswerModeQuestion(selectedAnswerMode.default_question ?? "");

    const resolvedModel =
      MODEL_CONFIG[selectedAnswerMode?.model ?? "gpt-6.1-sol"] ??
      MODEL_CONFIG["gpt-6.1-sol"] ??
      MODEL_CONFIG["gpt-5.6-terra"];

    setModel(resolvedModel);
    setReasoningEffort(
      normalizeReasoningEffortForModel(
        resolvedModel.name,
        selectedAnswerMode.reasoningEffort,
      ),
    );
  }, [selectedAnswerMode]);

  // The backend titles a chat from its last five turns; long answers add nothing to a title.
  const TITLE_TURNS = 5;
  const TITLE_MAX_CHARS = 1500;

  const summarizeChat = async (answers: Answers) => {
    const token = client ? await getToken(client) : undefined;
    let summary: string = answers[0][0];
    try {
      const messages: ResponseMessage[] = answers.slice(-TITLE_TURNS).flatMap((a) => [
        { content: a[0].slice(0, TITLE_MAX_CHARS), role: "user" },
        {
          content: (a[1].choices[0].message.content ?? "").slice(0, TITLE_MAX_CHARS),
          role: "assistant",
        },
      ]);
      const response = await summarizeChatApi(
        selectedUseCase,
        { messages },
        token,
      );
      if (response.ok) {
        const responseBody = await response.json();
        summary = responseBody.summary || summary;
      }
    } catch (e) {
      console.error(e);
    }
    return summary;
  };

  const newChat = async () => {
    setUploadedFilepaths([]);
    setSessionState(uuidv4());
    lastQuestionRef.current = "";
    error && setError(undefined);
    setActiveCitation(undefined);
    setActiveAnalysisPanelTab(undefined);
    setAnswers([]);
    setStreamedAnswers([]);
    setAnswerMode([]);
    setIsLoading(false);
    setIsStreaming(false);
    setShowScrollToBottom(false);
    setSelectedAnswerMode(defaultAnswerMode({}));
    setNumDocuments(0);
    lastScrolledLength.current = 0;
    targetScrollPosition.current = null;
    isScrollLocked.current = false;
    userMessageRefs.current = [];
    setIsGridExpanded(false);
    setStateSnapshot(null); // ADD — clear snapshot on new chat
    setSelectedTools([]);
    setSelectedSites(["auto"]);
    setLocationSelectorOpen(false);
    setModelSettingsPopupOpen(false);
  };

  const fetchUploadedDocuments = async (sessionState: string) => {
    const item = await indexedDBInstance.getDocs(sessionState);
    if (item) {
      setUploadedFilepaths(item.documents ?? []);
      setUploadedFilenames(item.filenames ?? []);
      setUploadedImages(item.image_filenames ?? []);
    } else {
      setUploadedFilepaths([]);
      setUploadedFilenames([]);
      setUploadedImages([]);
    }
  };

  const fileChipItems: FileChipItem[] = [
    // Already uploaded files
    ...uploadedFilenames
      .filter((name) => !pendingFiles.includes(name))
      .map((name) => ({ name, uploading: false })),
    // Currently uploading files
    ...pendingFiles.map((name) => ({ name, uploading: true })),
  ];

  const handleFileRemove = (filename: string) => {
    const index = uploadedFilenames.indexOf(filename);
    const path = index !== -1 ? uploadedFilepaths[index] : "";
    handleFileDeleteFromChatSession(path, filename);

    // Clean up thumbnail URL if it exists
    if (thumbnails[filename]) {
      URL.revokeObjectURL(thumbnails[filename]);
      setThumbnails((prev) => {
        const next = { ...prev };
        delete next[filename];
        return next;
      });
    }
  };

  const handleFileDeleteFromChatSession = async (
    pathToDelete: string,
    filenameToDelete: string,
  ) => {
    // 1. Try to find by Filename first (most reliable)
    let index = uploadedFilenames.indexOf(filenameToDelete);

    // 2. If not found, try to find by Path
    if (index === -1 && pathToDelete) {
      index = uploadedFilepaths.indexOf(pathToDelete);
    }

    // 3. Safety Check
    if (index === -1) {
      console.warn(`Could not find file to delete: ${filenameToDelete}`);
      return;
    }

    // 4. Atomic Update: Remove from both arrays at the specific index
    const updatedPaths = [...uploadedFilepaths];
    const updatedNames = [...uploadedFilenames];

    updatedPaths.splice(index, 1);
    updatedNames.splice(index, 1);

    setUploadedFilepaths(updatedPaths);
    setUploadedFilenames(updatedNames);

    // Filter images by name
    setUploadedImages((prev) =>
      prev.filter((name) => name !== filenameToDelete),
    );

    // Reset Answer Mode if we just deleted the last file
    if (updatedPaths.length === 0) {
      setSelectedAnswerMode(defaultAnswerMode({}));
      // Assuming SetPlaceholder is available in your scope
      SetPlaceholder(defaultAnswerMode({}).placeholder);
    }
  };

  const onUploadComplete = (p: string[], n: string[], i: string[]) => {
    setUploadedFilepaths((prev) => [...prev, ...p]);
    setUploadedFilenames((prev) => [...prev, ...n]);
    setUploadedImages((prev) => [...prev, ...i]);
  };

  const { isHandling, handleUploadFiles, handleRemoveFile } =
    useFileUpload(
      sessionState,
      onUploadComplete,
      handleFileDeleteFromChatSession,
    );

  const onFilesAdded = async (files: File[]) => {
    const names = files.map((f) => f.name);

    // 1. Show files immediately with uploading state
    setPendingFiles((prev) => [...prev, ...names]);

    // 2. Generate thumbnails for images
    files.forEach((file) => {
      if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name)) {
        const url = URL.createObjectURL(file);
        setThumbnails((prev) => ({ ...prev, [file.name]: url }));
      }
    });

    // 3. Start actual upload
    await handleUploadFiles(files);

    // 4. Upload done — remove from pending
    setPendingFiles((prev) => prev.filter((n) => !names.includes(n)));
  };

  const checkFilesEmptyChat = async () => {
    if (answers.length === 0 && uploadedFilepaths.length > 0) {
      let processedFiles = uploadedFilepaths.map(
        (file) => file.split("%2F")[1],
      );
      const interimAnswer: [string, ChatAppResponse] = [
        "Uploaded files: " + processedFiles.join(", "),
        {
          choices: [
            {
              index: 0,
              message: {
                content:
                  "Thanks for uploading. I am happy to answer your document related questions.",
                role: "assistant",
              },
              context: { thoughts: null, data_points: [] },
              session_state: sessionState,
              timestamp: Math.floor(Date.now() / 1000),
            },
          ],
        },
      ];
      await indexedDBInstance.addItem(
        sessionState,
        [interimAnswer],
        [defaultAnswerMode({})],
        uploadedFilepaths,
        [],
        [],
        undefined,
        undefined,
        null,
        selectedSites,
      );
      refreshHistoryPanel();
    }
  };

  const handleChatSessionWithDocsDelete = async (
    sessionId: string,
    docIds: string[],
  ) => {
    newChat();

    if (docIds.length) {
      // Retrieve the token from the client
      const token = client ? await getToken(client) : undefined;

      // Ensure we only call the API if the token exists
      if (token) {
        await deleteAllUploadedFilesApi(selectedUseCase, token, sessionId);
      } else {
        console.error(
          "Failed to delete files: No authentication token available.",
        );
      }
    }
  };

  const handleItemSelected = (id: string) => {
    setSessionState(id);
    setIsGridExpanded(false);
  };

  // CHANGED: wrapped in useCallback so child memoization isn't busted on every
  // streaming commit (Chat re-renders ~30 times/sec during streaming).
  const onShowCitation = useCallback(
    (c: string, i: number) => {
      if (
        activeCitation === c &&
        activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab &&
        selectedAnswer === i
      )
        setActiveAnalysisPanelTab(undefined);
      else {
        setActiveCitation(c);
        setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
      }
      setSelectedAnswer(i);
    },
    [activeCitation, activeAnalysisPanelTab, selectedAnswer],
  );
  const onToggleTab = (t: AnalysisPanelTabs, i: number) => {
    if (activeAnalysisPanelTab === t && selectedAnswer === i)
      setActiveAnalysisPanelTab(undefined);
    else setActiveAnalysisPanelTab(t);
    setSelectedAnswer(i);
  };

  // Stable callbacks for the memoized rows; they always call the latest handlers.
  const latestHandlers = useRef({ onShowCitation, onToggleTab, handleEditLastMessage, makeApiRequest });
  latestHandlers.current = { onShowCitation, onToggleTab, handleEditLastMessage, makeApiRequest };
  const interactionHandlers = useMemo<InteractionHandlers>(
    () => ({
      setUserMessageRef: (index, el) => {
        userMessageRefs.current[index] = el;
      },
      showCitation: (citation, index) => latestHandlers.current.onShowCitation(citation, index),
      toggleTab: (tab, index) => latestHandlers.current.onToggleTab(tab, index),
      edit: (question) => latestHandlers.current.handleEditLastMessage(question),
      retry: (question) => {
        void latestHandlers.current.makeApiRequest(question);
      },
    }),
    [],
  );
  const handleAnswerModeChosen = (m: AnswerMode) => setSelectedAnswerMode(m);
  const onStopClick = async () => {
    if (abortController) abortController.abort();
  };
  const togglePopup = () => setModelSettingsPopupOpen((prev) => !prev);

  // -----------------------------------------------------------------------
  // 5. RENDER
  // -----------------------------------------------------------------------
  // Extract this above the return statement
  const inputSection = (
    <div className={styles.bottomModeCommandsContainer}>
      {locationSelectorOpen && availableSiteMappings.length > 0 && (
        <LocationSelector
          isOpen={locationSelectorOpen}
          onClose={() => setLocationSelectorOpen(false)}
          buttonRef={locationSelectorButtonRef}
          siteMappings={availableSiteMappings}
          selectedSites={selectedSites}
          onChange={setSelectedSites}
        />
      )}

      {/* Model settings popup — unchanged */}
      {modelSettingsPopupOpen && (
        <ModelSettingsPopup
          isOpen={modelSettingsPopupOpen}
          onClose={() => setModelSettingsPopupOpen(false)}
          onSave={(m, r, s) => {
            setModel(m);
            setShowReasoning(s);
            const normalizedReasoningEffort = normalizeReasoningEffortForModel(
              m.name,
              r,
            );
            setReasoningEffort(normalizedReasoningEffort);
            setSelectedAnswerMode((prevMode) => ({
              ...prevMode,
              model: m.name,
              reasoningEffort: normalizedReasoningEffort,
            }));
          }}
          model={model}
          reasoningEffort={
            reasoningEffort ?? selectedAnswerMode.reasoningEffort
          }
          buttonRef={modelSettingsPopupButtonRef}
          showReasoning={showReasoning}
          availableModels={availableModels}
        />
      )}

      {/* ── FIX: UploadPopup uses new simplified API ──────── */}
      {/* No ref, no sessionState, no onUploadComplete — just onFilesAdded */}
      <UploadPopup
        isOpen={uploadPopupOpen}
        onClose={() => setUploadPopupOpen(false)}
        onFilesAdded={onFilesAdded}
        buttonRef={uploadButtonRef}
      />

      <div
        className={`
          ${styles.chatInput} 
          ${!showAnswerModeContainer ? styles.chatInputStandalone : ""}
        `}
      >
        {/* ── FIX: FileChips uses `files` prop, not `filenames` ── */}
        <FileChips
          files={fileChipItems}
          thumbnails={thumbnails}
          onRemove={handleFileRemove}
        />

        {/* ── Text input ───────────────────────────────────── */}
        <div className={styles.chatInputTopRow}>
          <QuestionInput
            ref={questionInputRef}
            placeholder={placeholder}
            default_question={answerModeQuestion}
            disabled={isLoading || isStreaming}
            onSend={handleSend}
            sessionState={sessionState}
            // ── FIX: use parent's onFilesAdded directly ─────
            // (was: uploadFileRef.current?.uploadFiles(f))
            onFilesAdded={onFilesAdded}
            onCanSendChange={setCanSend}
          />
        </div>

        {/* ── Bottom toolbar ───────────────────────────────── */}
        <div className={styles.chatInputToolbar}>
          <div className={styles.toolbarLeft}>
            {showUserUpload && (
              <UploadButton
                ref={uploadButtonRef}
                onClick={() => setUploadPopupOpen(!uploadPopupOpen)}
                isActive={uploadPopupOpen}
              />
            )}

            {availableTools.length > 0 && (
              <ToolSelector
                availableTools={availableTools}
                selectedTools={selectedTools}
                onToolsChange={setSelectedTools}
                currentModel={model.name}
                disabled={uploadedFilenames.length > 0}
              />
            )}
          </div>

          <div className={styles.toolbarRight}>
            {availableSiteMappings.length > 0 && (
              <LocationSelectorButton
                ref={locationSelectorButtonRef}
                onClick={() => setLocationSelectorOpen((prev) => !prev)}
                isOpen={locationSelectorOpen}
                selectedSites={selectedSites}
              />
            )}
            {showModelSettings && (
              <ModelSettingsButton
                ref={modelSettingsPopupButtonRef}
                onClick={togglePopup}
                isOpen={modelSettingsPopupOpen}
                modelDisplayName={model.displayName}
                modelProvider={model.provider}
              />
            )}
            {/* ── FIX: block send while files are uploading ── */}
            <SendButton
              onClick={() => questionInputRef.current?.send()}
              disabled={isLoading || !canSend || pendingFiles.length > 0}
              isStreaming={isStreaming}
              onStop={onStopClick}
            />
          </div>
        </div>

        {showAnswerModeContainer && (
          <AnswerModeContainer
            onAnswerModeChosen={handleAnswerModeChosen}
            numDocuments={numDocuments}
            selectedAnswerMode={selectedAnswerMode}
            isExpanded={isGridExpanded}
            setIsExpanded={setIsGridExpanded}
            onModalStateChange={(isOpen) => setIsChildModalOpen(isOpen)}
            refreshKey={answerModeRefreshKey}
          />
        )}
      </div>

      <span className={styles.disclaimer}>
        AI-generated content may be inaccurate. Always verify with appropriate
        professionals.
      </span>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.chatRoot}>
        <div style={{ flexShrink: 0 }}>
          <HistoryPanel
            notify={historyPanelVersion}
            onChatSelected={(
              hist,
              mode,
              paths,
              names,
              imgs,
              error,
              snapshot,
              historySelectedSites,
            ) => {
              checkFilesEmptyChat();
              setAnswers(hist);
              setStreamedAnswers(hist);
              setAnswerMode(mode);
              setHistorySelected(true);
              setUploadedFilepaths(paths);
              setUploadedFilenames(names);
              setUploadedImages(imgs);
              setStateSnapshot(snapshot ?? null); // ADD — restore snapshot
              setSelectedSites(
                historySelectedSites.length > 0 ? historySelectedSites : ["auto"],
              );
              if (error) {
                setError(error);
              } else {
                setError(undefined);
              }
              lastQuestionRef.current = hist[hist.length - 1][0];
            }}
            onDelete={handleChatSessionWithDocsDelete}
            onItemSelected={handleItemSelected}
            sessionState={sessionState}
            disabled={isStreaming || isLoading}
            indexedDBInstance={indexedDBInstance}
            isCollapsed={isHistoryCollapsed}
            onToggleCollapse={() => setIsHistoryCollapsed((prev) => !prev)}
            onNewChat={newChat}
          />
        </div>

        <div className={styles.chatContainer}>
          {!lastQuestionRef.current ? (
            /* ── EMPTY STATE: logo + input centered ── */
            <div className={styles.chatEmptyStateWithInput}>
              <div className={styles.greetingContainer}>
                <div className={styles.greetingRow}>
                  <img
                    src={mbenergy_logo}
                    alt="MB Energy Logo"
                    className={styles.greetingLogo}
                  />
                  <h1 className={styles.greetingText}>
                    Moin{firstName ? `, ${firstName}` : ""}
                  </h1>
                </div>
              </div>
              {inputSection}
            </div>
          ) : (
            /* ── HAS MESSAGES: normal layout ── */
            <>
              <div className={styles.chatMessageContainer}>
                {showScrollToBottom && !isChildModalOpen && (
                  <button
                    className={styles.scrollToBottomButton}
                    onClick={() => scrollToBottom()}
                  >
                    ↓
                  </button>
                )}
                <div
                  className={styles.chatMessageStream}
                  ref={chatContainerRef}
                  onScroll={handleScroll}
                >
                  {interactions.map((interaction, index) => {
                    const isLastItem = index === interactions.length - 1;

                    let separatorLabel = "";
                    const currentDate = getDateFromTimestamp(
                      interaction[1].choices[0].timestamp,
                    );
                    if (currentDate) {
                      if (index === 0) {
                        separatorLabel = getSeparatorLabel(currentDate);
                      } else {
                        const prevDate = getDateFromTimestamp(
                          interactions[index - 1][1].choices[0].timestamp,
                        );
                        if (!prevDate || !isSameDay(currentDate, prevDate)) {
                          separatorLabel = getSeparatorLabel(currentDate);
                        }
                      }
                    }

                    return (
                      <ChatInteraction
                        key={index}
                        index={index}
                        interaction={interaction}
                        answerMode={answerMode[index] || selectedAnswerMode}
                        separatorLabel={separatorLabel}
                        isLastItem={isLastItem}
                        showLoading={
                          isLastItem &&
                          isLoading &&
                          !interaction[1].choices[0].message.content
                        }
                        isStreaming={isLastItem && isStreaming}
                        isSelected={
                          selectedAnswer === index &&
                          activeAnalysisPanelTab !== undefined
                        }
                        disableEdit={isLoading || isStreaming}
                        error={isLastItem && error ? error.toString() : undefined}
                        reasoningResponse={isLastItem ? reasoningResponse : ""}
                        showReasoning={showReasoning}
                        handlers={interactionHandlers}
                      />
                    );
                  })}
                  {/* ── CREATE AI ASSISTANT BUTTON ── */}
                  {interactions.length >= 2 && (
                    <div className={styles.createAssistantRow}>
                      <button
                        className={styles.createAssistantBtn}
                        onClick={handleCreatePromptFromConversation}
                        disabled={
                          isExtractingPrompt || isLoading || isStreaming
                        }
                        title="Analyse this conversation and pre-fill a new System Prompt"
                      >
                        {isExtractingPrompt ? (
                          <span className={styles.createAssistantBtnInner}>
                            <span className={styles.createAssistantSpinner} />
                            Analysing conversation…
                          </span>
                        ) : (
                          <span className={styles.createAssistantBtnInner}>
                            <Beaker20Regular
                              className={styles.createAssistantIcon}
                            />
                            <span className={styles.createAssistantLabel}>
                              Create System Prompt from this conversation
                            </span>
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                  <div />
                </div>
              </div>

              <div className={styles.bottomContainer}>{inputSection}</div>
            </>
          )}
        </div>

        {interactions.length > 0 && activeAnalysisPanelTab && (
          <AnalysisPanel
            className={styles.chatAnalysisPanel}
            activeCitation={activeCitation}
            onActiveTabChanged={(x) => onToggleTab(x, selectedAnswer)}
            citationHeight="810px"
            answer={interactions[selectedAnswer][1]}
            activeTab={activeAnalysisPanelTab}
          />
        )}
      </div>
      {/* PROMPT-FROM-CONVERSATION MODAL */}
      <CreateAnswerModeModal
        isOpen={isPromptModalOpen}
        onClose={() => {
          setIsPromptModalOpen(false);
          setPromptModalInitialMode(null);
        }}
        onSave={handlePromptModalSave}
        onDelete={async (id) => {
          await customDB.deleteMode(id);
        }}
        nextOrderIndex={999}
        existingModeNames={[]} // pass your existing names if available
        initialMode={promptModalInitialMode}
        externalIsGenerating={isExtractingPrompt}
        externalGenerationProgress={extractionProgress}
        externalDraft={externalDraft}
      />
    </div>
  );
};

export default Chat;
